import * as React from 'react';
import { cn } from '@lib/utils';

export interface ToggleSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
}

const ToggleSwitch = React.forwardRef<HTMLInputElement, ToggleSwitchProps>(
  ({ checked, onCheckedChange, id, className, disabled, ...props }, ref) => {
    return (
      <label
        className={cn(
          "relative inline-flex items-center cursor-pointer",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
          {...props}
        />
        <div
          className={cn(
            // Base styles
            "relative w-11 h-6 bg-gray-200 rounded-full transition-all duration-300 ease-in-out",

            // Checked state
            "peer-checked:bg-brand-primary",

            // Focus state for accessibility
            "peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary-300/20",

            // Disabled state
            "peer-disabled:opacity-50 peer-disabled:cursor-not-allowed",

            // Subtle shadow for depth
            "shadow-inner"
          )}
          style={{
            backgroundColor: checked ? 'var(--brand-primary)' : 'var(--gray-200)'
          }}
        >
          <div
            className={cn(
              // Base thumb styles
              "absolute top-0.5 left-0.5 bg-white rounded-full transition-all duration-300 ease-in-out w-5 h-5 shadow-md",

              // Hover state when not disabled
              "peer-hover:not(:disabled):scale-110",

              // Active state
              "peer-active:scale-95"
            )}
            style={{
              transform: checked ? 'translateX(1.25rem)' : 'translateX(0)'
            }}
          />
        </div>
      </label>
    );
  },
);

ToggleSwitch.displayName = 'ToggleSwitch';

export { ToggleSwitch };
